import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';

import {
  TreeItem,
  TreeItemProps,
  treeItemClasses,
} from '@mui/x-tree-view/TreeItem';
import { Box, SvgIconProps, Typography } from '@mui/material';
import { designTokens } from '@platform-ui/design-system';
import { BLANK_VALUE } from '../constants';
import { removeCookie } from '../utils/removeCookie';
import { MultiOrgCookies, WorkingOrgCookie } from '../hooks/useMultiOrgCookies';

declare module 'react' {
  interface CSSProperties {
    '--tree-view-color'?: string;
    '--tree-view-bg-color'?: string;
  }
}

// Color mapping mirrors the OneID frontend (app-builder) convention so
// environment pills look consistent across products. See
// app-builder/src/projects/universal-identity/src/routes/UserLandingPage.tsx
// (ChipColors) and AdminKeyAssignmentView.tsx for the source of truth.
//
// NOTE: `darkRed500` and `midnight500` resolve to the same hex (#81074C) in the
// shared design tokens, which previously caused PROD and DSBX pills to appear
// identical (GEN-11215). Each value below MUST be visually distinct.
export const environmentsColorCodes = {
  prod: designTokens.colors.darkRed300,
  sbx: designTokens.colors.navy400,
  csbx: designTokens.colors.teal500,
  stg: designTokens.colors.gray600,
  dsbx: designTokens.colors.blue300,
  preprod: designTokens.colors.darkGreen900,
};

type StyledTreeItemProps = TreeItemProps & {
  bgColor?: string;
  bgColorForDarkMode?: string;
  color?: string;
  colorForDarkMode?: string;
  labelIcon: React.ElementType<SvgIconProps>;
  labelText: string;
  environment: keyof typeof environmentsColorCodes;
  targetUrl?: string;
  tenandId: string;
};

const StyledChipText = styled('span')({
  display: 'block',
  textAlign: 'center',
});

const StyledChip = styled('div')({
  minWidth: '45px',
  height: '20px',
  borderRadius: '100000px',
  color: 'white',
  fontSize: '11px',
  fontWeight: '550',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textTransform: 'uppercase',
});

const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: 'transparent',
  },
  [`& .${treeItemClasses.content}`]: {
    color: theme.palette.text.secondary,
    // borderTopRightRadius: theme.spacing(2),
    // borderBottomRightRadius: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    padding: '0px',
    minHeight: '36px',
    fontWeight: theme.typography.fontWeightMedium,
    borderBottom: '1px solid #0000001f',

    '&.Mui-expanded': {
      fontWeight: theme.typography.fontWeightRegular,
    },
    '&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused': {
      backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
      color: 'var(--tree-view-color)',
    },
    [`& .${treeItemClasses.label}`]: {
      fontWeight: 'inherit',
      color: 'inherit',
      padding: '0px',
    },
    [`& .${treeItemClasses.iconContainer}`]: {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      margin: '0px',
      width: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'stretch',
    },
  },
  [`&.no-children .${treeItemClasses.content} .${treeItemClasses.iconContainer}`]:
    {
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
})) as unknown as typeof TreeItem;

const StyledTreeItem = React.forwardRef(function StyledTreeItem(
  props: StyledTreeItemProps,
  ref: React.Ref<HTMLLIElement>,
) {
  const theme = useTheme();
  const {
    bgColor,
    color,
    labelIcon: LabelIcon,
    labelText,
    colorForDarkMode,
    bgColorForDarkMode,
    environment,
    targetUrl,
    tenandId,
    ...other
  } = props;

  const styleProps = {
    '--tree-view-color':
      theme.palette.mode !== 'dark' ? color : colorForDarkMode,
    '--tree-view-bg-color':
      theme.palette.mode !== 'dark' ? bgColor : bgColorForDarkMode,
  };

  const hasChildren = React.Children.count(props.children) > 0;

  // Apply a different className based on whether there are children
  const itemClassName = hasChildren ? '' : 'no-children';

  const handleAppSwitcherClick = () => {
    removeCookie(MultiOrgCookies);
    removeCookie(WorkingOrgCookie);
  };

  const StyledAnchor = styled('a')(() => ({
    textDecoration: 'none',
    color: 'inherit',
  }));

  const StyledTreeItemRootLabel = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 0.5,
        pr: 0,
        ':hover': hasChildren
          ? {
              backgroundColor: theme.palette.action.hover,
            }
          : {},
      }}
    >
      <Box component={LabelIcon} color="inherit" sx={{ mr: 1 }} />
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'inherit', flexGrow: 1 }}>
          {labelText}
        </Typography>
        <Box
          sx={{
            fontSize: '9px',
            color: '#949494',
            fontWeight: 400,
            'margin-top': '-1px',
            lineHeight: 'normal',
          }}
        >
          {tenandId}
        </Box>
      </Box>
      <StyledChip
        sx={{
          backgroundColor: environment
            ? environmentsColorCodes[environment]
            : designTokens.colors.blueGray800,
          marginRight: '5px',
        }}
      >
        <StyledChipText>{environment || BLANK_VALUE}</StyledChipText>
      </StyledChip>
    </Box>
  );

  const StyledTreeItemRootComponent = (
    <StyledTreeItemRoot
      className={itemClassName}
      label={
        hasChildren ? (
          <StyledAnchor
            href={targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleAppSwitcherClick}
          >
            {StyledTreeItemRootLabel}
          </StyledAnchor>
        ) : (
          StyledTreeItemRootLabel
        )
      }
      style={styleProps}
      {...other}
      ref={ref}
    >
      {props.children}
    </StyledTreeItemRoot>
  );

  return !hasChildren ? (
    <StyledAnchor
      href={targetUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleAppSwitcherClick}
    >
      {StyledTreeItemRootComponent}
    </StyledAnchor>
  ) : (
    StyledTreeItemRootComponent
  );
});

export default StyledTreeItem;
